import type { NewsArticle, Person, WithContext } from 'schema-dts';

import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import { resize } from '#utils/image/resize';

import { buildAuthorName } from '../../builders/buildAuthorName';
import { getImageAltText, getImageCopyright, getImageUrl } from '../../image';

import type { BuildArticleStructuredDataFragment } from './__generated__/buildArticleStructuredData.generated';
import { publisher } from './constants';

function getArticleAuthors(article: BuildArticleStructuredDataFragment, appUrl: string) {
	const nodes = article?.articlesAuthorsByArticleId?.nodes || [];
	const authors = nodes
		.map((node) => node?.authorByAuthorGuid)
		.filter(notNullOrUndefined)
		.map((author): Person => ({
			'@type': 'Person',
			name: buildAuthorName(author) ?? undefined,
			url: author.path && author.isActive ? `${appUrl}/autoren${author.path}` : undefined,
		}));

	return authors;
}

export function buildArticleStructuredData(article: BuildArticleStructuredDataFragment, appUrl: string, selfUrl: string) {
	const title = article.seoTitle || article.title;

	const articleSection = article?.categoryByPrimaryCategory?.rowId;
	const authors = getArticleAuthors(article, appUrl);
	const teaserImage = getImageUrl(article.image);
	const teaserImageAltText = getImageAltText({ image: article.image, seoTitle: article.seoTitle, title: article.title }) || '';
	const teaserImageCopyright = getImageCopyright(article.image) || '';

	const teaserImageResized1x1 = teaserImage ? resize(teaserImage, 'square', 'xsl') : undefined;
	const teaserImageResized4x3 = teaserImage ? resize(teaserImage, 'portrait', 'xsl') : undefined;
	const teaserImageResized16x9 = teaserImage ? resize(teaserImage, 'landscape', 'xsl') : undefined;

	const schema: WithContext<NewsArticle> = {
		'@context': 'https://schema.org',
		'@type': 'NewsArticle',
		articleSection,
		author: authors,
		dateModified: article.updatedAt ?? undefined,
		datePublished: article.firstPublicationDate ?? undefined,
		description: article.teaserText ?? undefined,
		headline: title,
		image: [
			{
				'@type': 'ImageObject',
				caption: teaserImageAltText,
				height: { '@type': 'QuantitativeValue', value: 1200, unitCode: 'px' },
				width: { '@type': 'QuantitativeValue', value: 1200, unitCode: 'px' },
				url: teaserImageResized1x1,
				copyrightHolder: {
					'@type': 'Person',
					name: teaserImageCopyright,
				},
			},
			{
				'@type': 'ImageObject',
				caption: teaserImageAltText,
				height: { '@type': 'QuantitativeValue', value: 900, unitCode: 'px' },
				width: { '@type': 'QuantitativeValue', value: 1200, unitCode: 'px' },
				url: teaserImageResized4x3,
				copyrightHolder: {
					'@type': 'Person',
					name: teaserImageCopyright,
				},
			},
			{
				'@type': 'ImageObject',
				caption: teaserImageAltText,
				height: { '@type': 'QuantitativeValue', value: 675, unitCode: 'px' },
				width: { '@type': 'QuantitativeValue', value: 1200, unitCode: 'px' },
				url: teaserImageResized16x9,
				copyrightHolder: {
					'@type': 'Person',
					name: teaserImageCopyright,
				},
			},
		],
		keywords: article.tags2.nodes.filter(notNullOrUndefined).map((node) => node.text),
		mainEntityOfPage: article.shareUrl,
		publisher: publisher(selfUrl),
		url: article.shareUrl,
	};

	return schema;
}
